'use client'

import Image from 'next/image'
import { useLocale, useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useId, useRef } from 'react'
import { IconArrowUpRight } from '@tabler/icons-react'
import { trackEvent } from '@/lib/analytics'
import {
  buildMoyuAnalyticsPayload,
  buildMoyuPromoUrl,
  type MoyuPromoVariant,
  type MoyuPromoSurface
} from '@/lib/moyu'
import { cn } from '@/lib/utils'

export function MoyuCTA({
  variant,
  surface,
  className
}: {
  variant: MoyuPromoVariant
  surface: MoyuPromoSurface
  className?: string
}) {
  const t = useTranslations('moyuPromo')
  const locale = useLocale()
  const pathname = usePathname()
  const rootRef = useRef<HTMLElement>(null)
  const hasTrackedImpression = useRef(false)
  const headingId = useId()
  const href = buildMoyuPromoUrl(locale, surface)

  useEffect(() => {
    const node = rootRef.current
    if (!node || hasTrackedImpression.current) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || entry.intersectionRatio < 0.5) return
        hasTrackedImpression.current = true
        trackEvent(
          'moyu_promo_impression',
          buildMoyuAnalyticsPayload({
            variant,
            surface,
            locale,
            pagePath: pathname
          })
        )
        observer.disconnect()
      },
      { threshold: [0.5] }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [locale, pathname, surface, variant])

  const trackClick = () => {
    trackEvent('moyu_promo_clicked', {
      ...buildMoyuAnalyticsPayload({
        variant,
        surface,
        locale,
        pagePath: pathname
      }),
      destination: href
    })
  }

  if (variant === 'strip') {
    return (
      <aside
        ref={rootRef}
        aria-labelledby={headingId}
        className={cn(
          'relative isolate flex min-h-[72px] w-full items-center gap-3 overflow-hidden rounded-[20px] bg-[#ff7a66] px-3 py-2 text-[#202328] sm:gap-4 sm:px-4',
          className
        )}
      >
        <Image
          src="/images/moyu/icon5.webp"
          alt=""
          width={160}
          height={160}
          className="relative h-12 w-12 shrink-0 object-contain sm:h-14 sm:w-14"
        />
        <div className="relative min-w-0 flex-1 sm:flex sm:items-center sm:justify-between sm:gap-5">
          <div>
            <p
              id={headingId}
              className="text-sm leading-snug font-semibold text-balance sm:text-base"
            >
              {t('strip.title')}
            </p>
            <p className="mt-0.5 hidden text-xs leading-relaxed text-[#202328]/72 md:block">
              {t('strip.description')}
            </p>
          </div>
          <a
            href={href}
            onClick={trackClick}
            className="mt-2 inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-[#202328] px-4 text-xs font-semibold text-[#fffaf0] transition-transform hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-offset-3 focus-visible:outline-[#202328] motion-reduce:transform-none motion-reduce:transition-none sm:mt-0"
          >
            {t('strip.button')}
            <IconArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </aside>
    )
  }

  return (
    <aside
      ref={rootRef}
      aria-labelledby={headingId}
      className={cn(
        'relative isolate min-h-[390px] w-full overflow-visible rounded-[25px] bg-[#ff7a66] px-6 pt-7 pb-[170px] text-[#202328] sm:min-h-[340px] sm:rounded-[30px] sm:px-[clamp(28px,6vw,72px)] sm:py-10',
        className
      )}
    >
      <div className="relative z-10 max-w-[680px]">
        <h2
          id={headingId}
          className="max-w-[620px] text-[clamp(2rem,4vw,3.25rem)] leading-[1.08] font-semibold tracking-[-0.025em] text-balance"
        >
          {t('full.title')}
        </h2>
        <p className="mt-4 max-w-[570px] text-[1.08rem] leading-relaxed text-[#202328]/78">
          {t('full.description')}
        </p>
        <a
          href={href}
          onClick={trackClick}
          className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#202328] px-5 text-sm font-semibold text-[#fffaf0] transition-transform hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-[#202328] motion-reduce:transform-none motion-reduce:transition-none"
        >
          {t('full.button')}
          <IconArrowUpRight className="h-4 w-4" />
        </a>
      </div>
      <Image
        src="/images/moyu/celebrate.webp"
        alt=""
        width={900}
        height={801}
        sizes="(max-width: 640px) 215px, 285px"
        className="pointer-events-none absolute right-[-3%] bottom-[-7%] z-0 h-auto w-[215px] rotate-[-4deg] object-contain sm:right-[-2%] sm:bottom-[-9%] sm:w-[min(285px,32vw)]"
      />
    </aside>
  )
}
